import { useCallback, useState } from "react";
import type { ServerMessage } from "../api/types";

export type ChatRole = "user" | "assistant" | "thinking" | "system" | "error";

export interface ChatBubble {
  id: string;
  role: ChatRole;
  text: string;
  /** True while the assistant is still streaming this bubble. */
  streaming?: boolean;
}

export interface UsageTotals {
  input: number;
  output: number;
  cacheRead: number;
  cacheCreate: number;
}

const ZERO_USAGE: UsageTotals = { input: 0, output: 0, cacheRead: 0, cacheCreate: 0 };

let counter = 0;
function nextId(prefix: string): string {
  counter += 1;
  return `${prefix}-${Date.now()}-${counter}`;
}

interface State {
  bubbles: ChatBubble[];
  usage: UsageTotals;
  running: boolean;
}

const INITIAL: State = { bubbles: [], usage: ZERO_USAGE, running: false };

/**
 * Reduces server messages into a chat transcript + usage totals + running flag.
 * The transcript is intentionally simple in Phase 1 — tool events render as
 * compact system rows. Per-tool rich rendering can land alongside the per-mode
 * wizards in later phases.
 */
export function useChatTranscript() {
  const [state, setState] = useState<State>(INITIAL);

  const reset = useCallback(() => setState(INITIAL), []);

  const appendUser = useCallback((text: string) => {
    setState((prev) => ({
      ...prev,
      running: true,
      bubbles: [...prev.bubbles, { id: nextId("u"), role: "user", text }],
    }));
  }, []);

  const handleServer = useCallback((msg: ServerMessage) => {
    setState((prev) => {
      switch (msg.type) {
        case "text":
          return appendStreaming(prev, "assistant", msg.content);
        case "thinking":
          return appendStreaming(prev, "thinking", msg.content);
        case "tool_event":
          return {
            ...prev,
            bubbles: [
              ...closeStreaming(prev.bubbles),
              { id: nextId("t"), role: "system", text: `▶ ${msg.tool}` },
            ],
          };
        case "error":
          return {
            ...prev,
            running: false,
            bubbles: [
              ...closeStreaming(prev.bubbles),
              { id: nextId("e"), role: "error", text: msg.message },
            ],
          };
        case "done":
          return { ...prev, running: false, bubbles: closeStreaming(prev.bubbles) };
        case "usage":
          return {
            ...prev,
            usage: {
              input: prev.usage.input + msg.input_tokens,
              output: prev.usage.output + msg.output_tokens,
              cacheRead: prev.usage.cacheRead + msg.cache_read_input_tokens,
              cacheCreate: prev.usage.cacheCreate + msg.cache_creation_input_tokens,
            },
          };
        default:
          return prev;
      }
    });
  }, []);

  return { ...state, reset, appendUser, handleServer };
}

function appendStreaming(prev: State, role: ChatRole, chunk: string): State {
  const last = prev.bubbles[prev.bubbles.length - 1];
  if (last && last.streaming && last.role === role) {
    const updated: ChatBubble = { ...last, text: last.text + chunk };
    return { ...prev, bubbles: [...prev.bubbles.slice(0, -1), updated] };
  }
  return {
    ...prev,
    bubbles: [...closeStreaming(prev.bubbles), { id: nextId(role), role, text: chunk, streaming: true }],
  };
}

function closeStreaming(bubbles: ChatBubble[]): ChatBubble[] {
  if (bubbles.length === 0) return bubbles;
  const last = bubbles[bubbles.length - 1];
  if (!last.streaming) return bubbles;
  return [...bubbles.slice(0, -1), { ...last, streaming: false }];
}
